import { computed, toRef } from 'vue'
import useStatus from '@nicolaudie-group/ng-ui-kit/composables/useStatus'
import useProfileStore, {
  brandsKey,
  constantsKey,
  profileKey,
  profileStatusKey,
} from '@/stores/profile.store'
import useModeStore from '@/stores/mode.store'
import useChannelStore from '@/stores/channel.store'
import usePresetRangeStore from '@/stores/presetRange.store'
import usePresetStore from '@/stores/preset.store'

export function useProfile() {
  const profileStore = useProfileStore()
  return toRef(profileStore, profileKey)
}

export function useProfileStatus() {
  const profileStore = useProfileStore()
  return useStatus(toRef(profileStore, profileStatusKey))
}

export function useModes() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'modes')
}

export function useMode() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'mode')
}

export function useModeStatus() {
  const modeStore = useModeStore()
  return useStatus(toRef(modeStore, 'status'))
}

export function useGlobalChannels() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'globalChannels')
}

export function useChannels() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'channels')
}

export function useChannel() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'channel')
}

export function useChannelStatus() {
  const channelStore = useChannelStore()
  return useStatus(toRef(channelStore, 'status'))
}

export function usePresetRanges() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'presetRanges')
}

export function usePresetRange() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'presetRange')
}

export function usePresetRangeStatus() {
  const presetRangeStore = usePresetRangeStore()
  return useStatus(toRef(presetRangeStore, 'status'))
}

export function usePresets() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'presets')
}

export function usePreset() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'preset')
}

export function usePresetStatus() {
  const presetStore = usePresetStore()
  return useStatus(toRef(presetStore, 'status'))
}

export function useObject() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'object')
}

export function useReport() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'report')
}

export function useConstants() {
  const profileStore = useProfileStore()
  return toRef(profileStore, constantsKey)
}

export function useBeams() {
  const profileStore = useProfileStore()
  return toRef(profileStore, 'beams')
}

export function useBrands() {
  const profileStore = useProfileStore()
  return computed(() => profileStore[brandsKey] || [])
}

export function useCurrent() {
  return {
    profile: useProfile(),
    modes: useModes(),
    mode: useMode(),
    channels: useChannels(),
    channel: useChannel(),
    presetRanges: usePresetRanges(),
    presetRange: usePresetRange(),
    presets: usePresets(),
    preset: usePreset(),
  }
}

export function useReadonly() {
  const profile = useProfile()
  const profileStatus = useProfileStatus()
  const modeStatus = useModeStatus()
  const channelStatus = useChannelStatus()
  const presetRangeStatus = usePresetRangeStatus()
  const presetStatus = usePresetStatus()

  const readonly = computed(() => {
    const { userCanEdit, isRemove } = profile.value || {}
    const profilePending = profileStatus.pending.value
    const modePending = modeStatus.pending.value
    const channelPending = channelStatus.pending.value
    const presetRangePending = presetRangeStatus.pending.value
    const presetPending = presetStatus.pending.value
    return (
      !userCanEdit
      || isRemove
      || profilePending
      || modePending
      || channelPending
      || presetRangePending
      || presetPending
    )
  })

  return readonly
}
